import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useSite } from "@/lib/site";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/stock", label: "Stock" },
  { to: "/stock/sold", label: "Sold" },
  { to: "/sell-your-4x4", label: "Sell Your 4x4" },
  { to: "/servicing", label: "Servicing", flag: "servicing_live" },
  { to: "/the-cirencester-standard", label: "The Cirencester Standard" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

// Pages behind a settings flag (drafts) only appear once published.
export const useNav = () => {
  const site = useSite();
  return NAV.filter((n) => !n.flag || site[n.flag]);
};

export default function Header() {
  const site = useSite();
  const nav = useNav();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const overHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${overHero ? "bg-transparent" : "bg-paper/90 backdrop-blur-md border-b border-stone"}`}
    >
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Logo light={overHero} />
        <nav aria-label="Main" className="hidden xl:flex items-center gap-8">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/" || n.to === "/stock"} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`link-u text-[0.8rem] font-medium tracking-wide ${overHero ? "text-paper" : "text-charcoal"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={`tel:${site.phone_href}`} data-testid="header-call" aria-label="Call us"
            className={`hidden sm:inline-flex items-center gap-2 text-sm font-medium ${overHero ? "text-paper" : "text-charcoal"}`}>
            <Phone className="h-4 w-4" strokeWidth={1.5} /> <span className="hidden lg:inline">{site.phone}</span>
          </a>
          <Link to="/stock" data-testid="header-view-stock" className={`btn ${overHero ? "btn-light" : "btn-primary"} !px-4 !py-3 sm:!px-5`}>View Stock</Link>
          <button type="button" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu"
            className={`xl:hidden p-2 ${overHero ? "text-paper" : "text-charcoal"}`}>
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {createPortal(
        <AnimatePresence>
          {open && <MobileMenu onClose={() => setOpen(false)} site={site} nav={nav} />}
        </AnimatePresence>,
        document.body,
      )}
    </header>
  );
}

function MobileMenu({ onClose, site, nav }) {
  return (
    <motion.div className="fixed inset-0 z-[60] xl:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <button aria-label="Close menu overlay" className="absolute inset-0 bg-charcoal/40" onClick={onClose} />
      <motion.aside data-lenis-prevent role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-menu"
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
        className="on-dark absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col bg-forest px-8 pb-10 pt-6 text-paper">
        <div className="flex justify-end">
          <button onClick={onClose} data-testid="mobile-menu-close" aria-label="Close menu" className="p-2"><X className="h-6 w-6" strokeWidth={1.5} /></button>
        </div>
        <nav className="mt-8 flex flex-col gap-5" aria-label="Mobile">
          {nav.map((n, i) => (
            <motion.div key={n.to} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
              <NavLink to={n.to} end={n.to === "/" || n.to === "/stock"} data-testid={`mobile-nav-${i}`} className="font-serif text-3xl">{n.label}</NavLink>
            </motion.div>
          ))}
        </nav>
        <div className="mt-auto space-y-3 border-t border-paper/20 pt-6 text-sm text-stone">
          <a href={`tel:${site.phone_href}`} className="flex items-center gap-2" data-testid="mobile-menu-call"><Phone className="h-4 w-4" /> {site.phone}</a>
          <p>Cirencester • The Cotswolds</p>
        </div>
      </motion.aside>
    </motion.div>
  );
}
